<template>
  <Onboarding>
    <!-- No Workspaces Message -->
    <div v-if="!workspacesLoading && !workspacesError && workspaceStore.workspaces.length === 0" class="my-24 w-full">
      <div class="mx-auto flex w-[420px] flex-col items-center justify-center gap-4 rounded-md bg-gray-800 p-8 shadow">
        <div class="text-surface-400 flex flex-col items-center justify-center gap-2 text-xl">
          <i class="pi pi-info-circle text-4xl"></i>
          <div class="flex flex-col items-center">
            <span class="text-surface-200 text-xl">No workspaces available</span>
            <span class="text-base"> Create one to get started. </span>
          </div>
        </div>
        <DButton
          label="Create Workspace"
          icon="pi pi-plus"
          @click="showCreateWorkspaceDialog = true"
          severity="primary" />
      </div>
    </div>
    <div class="mx-12 flex flex-1 flex-col md:mx-24 lg:mx-64" v-else>
      <!-- Page Header -->
      <div class="mb-8" v-if="!assetsError">
        <DButton v-if="assets.length" severity="primary">
          <div class="text-primary-950 flex items-center gap-2 font-semibold">
            Projects
            <span class="bg-primary-600 inline-flex h-6 w-9 items-center justify-center rounded-2xl p-0.5">{{
              assets.length
            }}</span>
          </div>
        </DButton>
      </div>

      <!-- Loading State -->
      <div v-if="assetsLoading" class="flex items-center justify-center py-12">
        <div class="text-center">
          <i class="pi pi-spinner pi-spin mb-2 text-2xl text-gray-400"></i>
          <p class="text-gray-400">Loading assets...</p>
        </div>
      </div>

      <!-- Assets Error -->
      <div v-else-if="assetsError" class="py-12 text-center">
        <i class="pi pi-exclamation-triangle mb-4 text-4xl text-red-400"></i>
        <h3 class="mb-2 text-lg font-medium text-white">Error Loading Assets</h3>
        <p class="mb-4 text-red-400">{{ assetsError }}</p>
        <DButton label="Try Again" @click="onWorkspaceChange" severity="secondary" />
      </div>

      <!-- Asset List Section -->
      <div v-else-if="selectedWorkspaceId && assets.length > 0 && !assetsError" class="rounded-lg">
        <div v-if="assets.length" class="grid gap-4 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 lg:gap-6">
          <DAsset
            type="asset"
            v-for="(asset, index) in assets"
            :key="index"
            :resource="asset"
            transition-effect
            shadow
            @show="viewAsset"
            @click="viewAsset(asset)"
            :default-menus="['']">
            <template #bottom>
              <div class="flex items-center justify-between text-gray-100">
                <span class="text-base md:text-lg" @click="viewAsset(asset)">
                  {{ asset.name || 'Untitled Asset' }}
                </span>
              </div>
            </template>
          </DAsset>
        </div>

        <DPaginator
          v-if="assetStore.pagination.total > assetStore.pagination.per_page"
          :rows="assetStore.pagination.per_page"
          :totalRecords="assetStore.pagination.total"
          @page="updateCurrentPage" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="selectedWorkspaceId && !assetsLoading && !assetsError && workspaceStore.workspaces.length > 0"
        class="mx-auto my-24 flex w-[420px] flex-col items-center justify-center rounded-lg bg-gray-800 p-8">
        <div class="text-center">
          <i class="pi pi-folder-open mb-4 text-4xl text-gray-400"></i>
          <h3 class="mb-2 text-xl font-medium text-white">No assets found</h3>
          <p class="mb-4 text-gray-400">This workspace doesn't have any assets yet.</p>
          <DButton
            label="Create New Mapping Matter Project"
            icon="pi pi-plus"
            @click="showNewProjectDialog = !showNewProjectDialog"
            severity="primary" />
        </div>
      </div>

      <!-- No Workspace Selected State -->
      <div
        v-else-if="!selectedWorkspaceId && workspaceStore.workspaces.length > 0"
        class="mx-auto my-24 flex w-[420px] flex-col items-center justify-center rounded-lg bg-gray-800 p-8">
        <div class="py-12 text-center">
          <i class="pi pi-folder mb-4 text-4xl text-gray-400"></i>
          <h3 class="mb-2 text-xl font-medium text-white">Select a workspace</h3>
          <p class="text text-gray-400">Choose a workspace from the dropdown above to browse its assets.</p>
        </div>
      </div>
    </div>
    <!-- Create Workspace Dialog -->
    <DDialog
      v-model:visible="showCreateWorkspaceDialog"
      header="Create New Workspace"
      :modal="true"
      :closable="true"
      class="w-full max-w-md"
      :draggable="false">
      <div class="space-y-4 p-4">
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-300"> Workspace Name </label>
          <DInputText
            v-model="newWorkspaceName"
            placeholder="Enter workspace name"
            class="w-full"
            :disabled="creatingWorkspace"
            @keyup.enter="createWorkspace" />
        </div>

        <div v-if="createWorkspaceError" class="rounded-lg border border-red-700 bg-red-900/20 p-3">
          <div class="text-sm text-red-400">
            <i class="pi pi-exclamation-triangle mr-1"></i>
            {{ createWorkspaceError }}
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <DButton label="Cancel" @click="cancelCreateWorkspace" :disabled="creatingWorkspace" />
          <DButton
            label="Create"
            icon="pi pi-plus"
            @click="createWorkspace"
            severity="primary"
            :loading="creatingWorkspace"
            :disabled="!newWorkspaceName.trim()" />
        </div>
      </template>
    </DDialog>

    <NewProjectDialog v-if="showNewProjectDialog" @close="showNewProjectDialog = !showNewProjectDialog" />
  </Onboarding>
</template>

<script setup>
import NewProjectDialog from '@/components/NewProjectDialog.vue';
import { useAssetStore } from '@/stores/api/asset';
import { useWorkspaceStore } from '@/stores/api/workspace';
import { useUserStore } from '@/stores/userStore';
import { DAsset, DButton, DDialog, DInputText, DPaginator } from '@disguise/cloud-ui';
import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import Onboarding from '../components/layout/Onboarding.vue';
import { useAppStore } from '../stores/appStore';

// Store instances
const workspaceStore = useWorkspaceStore();
const assetStore = useAssetStore();
const userStore = useUserStore();
const appStore = useAppStore();
const showNewProjectDialog = ref(false);
// Reactive data
const selectedWorkspaceId = ref(localStorage.getItem('crux-selected-workspace-id') || null);

const searchQuery = ref('');
const selectedAssetType = ref(null);
const workspacesLoading = ref(true);
const assetsLoading = ref(false);
const workspacesError = ref(null);
const assetsError = ref(null);
const showCreateWorkspaceDialog = ref(false);
const newWorkspaceName = ref('');
const creatingWorkspace = ref(false);
const createWorkspaceError = ref(null);
const router = useRouter();
const pagination = ref({
  page: 1,
  perPage: 12,
});
const collapse = ref(true);
const collapsedWidth = ref(80);
const expandedWidth = ref(150);

const sidebarMargin = computed(() => {
  const margin = collapse.value ? collapsedWidth.value : expandedWidth.value;
  return {
    marginLeft: `${margin}px`,
  };
});

const assets = computed(() => assetStore.assets || []);

const assetTypeOptions = computed(() => {
  const types = [...new Set(assets.value.map((asset) => asset.asset_type))];
  return types.map((type) => ({
    label: type.charAt(0).toUpperCase() + type.slice(1),
    value: type,
  }));
});

const filteredAssets = computed(() => {
  let filtered = [...assets.value];

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(
      (asset) =>
        asset.latest_version?.name.toLowerCase().includes(query) || asset.asset_type.toLowerCase().includes(query)
    );
  }

  // Filter by asset type
  if (selectedAssetType.value) {
    filtered = filtered.filter((asset) => asset.asset_type === selectedAssetType.value);
  }

  // Pagination
  const start = (pagination.value.page - 1) * pagination.value.perPage;
  const end = start + pagination.value.perPage;
  return filtered.slice(start, end);
});

// Methods
const loadWorkspaces = async () => {
  // Check if user is authenticated before making API calls
  if (!userStore.isAuthenticated) {
    await userStore.loginWithProvider('oidc_auth0', '/callback');
    return;
  }

  workspacesLoading.value = true;
  workspacesError.value = null;
  try {
    await workspaceStore.fetch().then((response) => {
      selectedWorkspaceId.value = workspaceStore.workspaces[0]?.code || null;
    });
  } catch (error) {
    console.error('Error loading workspaces:', error);
    workspacesError.value = 'Failed to load workspaces. Please try again.';
  } finally {
    workspacesLoading.value = false;
  }
};

const onWorkspaceChange = async () => {
  if (!selectedWorkspaceId.value) return;

  assetsLoading.value = true;
  assetsError.value = null;
  try {
    await assetStore.fetchForWorkspace(selectedWorkspaceId.value, {
      asset_type: 'CX',
      sort_by: assetStore.pagination.sort_by,
    });
  } catch (error) {
    console.error('Error loading assets:', error);
    assetsError.value = 'Failed to load assets. Please try again.';
  } finally {
    assetsLoading.value = false;
  }
};

const getAssetIcon = (type) => {
  const iconMap = {
    video: 'pi pi-video',
    image: 'pi pi-image',
    audio: 'pi pi-volume-up',
    document: 'pi pi-file',
    CX: 'pi pi-box',
    component: 'pi pi-cog',
    workspaceor: 'pi pi-video',
    led: 'pi pi-desktop',
    folder: 'pi pi-folder',
    default: 'pi pi-file',
  };
  return iconMap[type] || iconMap.default;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

const viewAsset = (asset) => {
  router.push(`/editor/${asset.latest_version.asset}`);
};

const createWorkspace = async () => {
  if (!newWorkspaceName.value.trim()) return;

  creatingWorkspace.value = true;
  createWorkspaceError.value = null;
  try {
    await workspaceStore.store({ name: newWorkspaceName.value.trim() });

    // Reload workspaces to get the newly created one
    await workspaceStore.fetch();

    // Select the newly created workspace
    if (workspaceStore.workspaces.length > 0) {
      const newWorkspace = workspaceStore.workspaces.find((w) => w.name === newWorkspaceName.value.trim());
      if (newWorkspace) {
        selectedWorkspaceId.value = newWorkspace.code;
        await onWorkspaceChange();
      }
    }

    // Close dialog and reset form
    showCreateWorkspaceDialog.value = false;
    newWorkspaceName.value = '';
  } catch (error) {
    console.error('Error creating workspace:', error);
    createWorkspaceError.value = error.response?.data?.message || 'Failed to create workspace. Please try again.';
  } finally {
    creatingWorkspace.value = false;
  }
};

const cancelCreateWorkspace = () => {
  showCreateWorkspaceDialog.value = false;
  newWorkspaceName.value = '';
  createWorkspaceError.value = null;
};

// Persist selected workspace to localStorage
watchEffect(() => {
  if (selectedWorkspaceId.value) {
    localStorage.setItem('crux-selected-workspace-id', selectedWorkspaceId.value);
  } else {
    localStorage.removeItem('crux-selected-workspace-id');
  }
});

// Helper function to handle workspace initialization after loading
const handleWorkspaceInitialization = async () => {
  await loadWorkspaces();

  // If we have a persisted workspace ID and workspaces are loaded, auto-load assets
  if (selectedWorkspaceId.value && workspaceStore.workspaces.length > 0) {
    // Verify the workspace still exists
    const workspaceExists = workspaceStore.workspaces.some((p) => p.code === selectedWorkspaceId.value);
    if (workspaceExists) {
      await onWorkspaceChange();
    } else {
      // Clear invalid workspace ID
      selectedWorkspaceId.value = null;
    }
  }
};

// Lifecycle
onMounted(async () => {
  // Wait for auth to be checked before attempting to load workspaces
  if (userStore.isLoading) {
    // Watch for loading to complete
    const unwatch = watch(
      () => userStore.isLoading,
      async (loading) => {
        if (!loading) {
          unwatch();
          await handleWorkspaceInitialization();
        }
      }
    );
  } else {
    // Auth has already finished loading
    await handleWorkspaceInitialization();
  }
});

const updateCurrentPage = async (event) => {
  if (!selectedWorkspaceId.value) return;
  appStore.setLoading(true);
  appStore.setDisplayLoadingToast(true);
  assetsError.value = null;
  try {
    await assetStore.fetchForWorkspace(selectedWorkspaceId.value, {
      asset_type: 'CX',
      page: event.page === assetStore.pagination.page || event.page === 0 ? event.page + 1 : event.page,
      sort_by: assetStore.pagination.sort_by,
    });
  } catch (error) {
    console.error('Error loading assets:', error);
    assetsError.value = 'Failed to load assets. Please try again.';
  } finally {
    appStore.setLoading(false);
    appStore.setDisplayLoadingToast(false);
  }
};

const menus = ref([
  {
    label: 'Files',
    active: false,
    icon: 'pi pi-folder',
    command: () => {
      router.push('/');
    },
  },
]);
</script>
